/**
 * Plan-read observability — "this can never fail silently again".
 *
 * The pre-call rail disappeared for every customer and nothing anywhere recorded it: the failure path was a
 * bare `.catch(() => {})`, the backend `check` package emits no metric, and `CHECK.ROLLUP.CHANGED` is a
 * reserved code with no emitter. The breach was invisible from every direction at once.
 *
 * This module is deliberately tiny and dependency-free. It cannot itself throw, because a telemetry failure
 * must never be the reason a customer cannot verify.
 */

export type PlanReadFailure = 'empty' | 'unauthorised' | 'not_found' | 'server' | 'network' | 'unknown';

/** Map a thrown API error onto a reportable cause without depending on the client's error class. */
export function classifyPlanReadFailure(e: unknown): PlanReadFailure {
  const status = (e as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return 'unauthorised';
  if (status === 404 || status === 405) return 'not_found';
  if (typeof status === 'number' && status >= 500) return 'server';
  if (typeof status === 'number') return 'unknown';
  return 'network';
}

/**
 * Record that a customer was shown the fail-closed path because their plan could not be read.
 *
 * Reports to the backend when a telemetry sink exists so the event lands on the audit chain; always leaves a
 * console breadcrumb so it is visible in a support session even when the sink is absent. Never throws, never
 * blocks the render, and carries NO customer data — session id and cause only.
 */
export function reportPlanReadFailure(sessionId: string, cause: PlanReadFailure): void {
  try {
    // biome-ignore lint/suspicious/noConsole: the breadcrumb is the point — this path was silent.
    console.warn(`[vcip] check-plan read failed: cause=${cause} session=${sessionId}`);
    const body = JSON.stringify({ sessionId, cause, at: new Date().toISOString() });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // Beacon survives the navigation to the fail-closed screen; a fetch would be cancelled.
      navigator.sendBeacon('/api/v1/telemetry/check-plan-read', new Blob([body], { type: 'application/json' }));
    }
  } catch {
    // Telemetry must never break the flow it is observing.
  }
}
